/* eslint-disable max-len */
/* eslint-disable react/no-array-index-key */
import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Dropdown from 'rc-dropdown';
import Menu, { Item as MenuItem } from 'rc-menu';
import styled from '@emotion/styled';
import IcomoonReact from 'icomoon-react';
import 'rc-dropdown/assets/index.css';
import 'rc-menu/assets/index.css';
import PropTypes from 'prop-types';
import moment from 'moment';

import iconSet from '../../../../shared/images/teambuildr-selection.json';
import Text from '../../../../shared/components/Text/Text';
import {
  setExpandedItems, updateWorkoutInfoColor, updateWorkoutInfoName, setActiveModal, shareWorkoutInfo, updateNonDateBasedWorkoutInfo,
} from '../../ducks/calendarActions';

/** Module-level timestamp of the most recent day-header dropdown close.
 * Set in `handleVisibleChange(false)`, which rc-trigger calls
 * synchronously from its outside-click handler — so by the time the
 * cell's onClick fires (one tick later), this stamp reliably reflects
 * "a menu just closed because of this click."
 *
 * Read by MonthDayContent's handleDayClick to suppress the cell's
 * switch-to-week-view navigation when the click was actually a menu
 * dismissal. Window is short enough to not eat genuine clicks. */
let lastDayDropdownClosedAt = 0;
export const wasCalendarDayDropdownJustClosed = (windowMs = 200) => (
  Date.now() - lastDayDropdownClosedAt < windowMs
);

const DropdownIcon = styled('div')`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const DropdownContainer = styled('div')`
  height: 28px;
  width: 28px;
  border-radius: 4px;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin-right: -5px;

  :hover {
    background: rgba(119, 167, 255, 0.2);
  }

  &:hover svg {
    fill: #77A7FF;
  }
`;

const MenuWrapper = styled('div')`
  .rc-menu {
    border-radius: 4px !important;
    box-shadow: 0px 4px 8px 0px #3031331A !important;
    border: 1px solid #EEEEEE !important;
    margin: 0px !important;
    min-width: 0px !important;

    padding-top: 8px;
    padding-bottom: 8px;
  }
  
  .rc-menu-item:hover {
    background-color: #F5F5F5 !important;
  }
  
  .rc-menu-submenu-title {
    padding-top: 7px !important;
    padding-bottom: 7px !important;
    padding-left: 14px !important;
    cursor: pointer !important;
  }
  
  .rc-menu-submenu-title:hover {
    background-color: #F5F5F5 !important;
  }
  
  .rc-menu-submenu-arrow {
    right: 15px !important;
  }
  
  .rc-menu-submenu-popup {
    z-index: 1001 !important;
  }
  
  .color-submenu {
    width: 35px !important;
    padding: 8px !important;
    top: 86px !important;
    left: -35px !important;
    position: absolute !important;
  }
`;

const CustomDivider = styled('div')`
  height: 1px;
  margin: 8px 0;
  background-color: #EEEEEE;

  margin-left: 18px;
  margin-right: 18px;
`;

const IconContainer = styled('div')`
  display: flex;
  align-items: center;
  width: 100%;
  svg {
    margin-right: 8px;
  }
`;

const ColorIconContainer = styled('div')`
  display: flex;
  align-items: center;
`;

const NoColorIconContainer = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #9E9E9E;
  height: 24px;
  width: 24px;
  border-radius: 4px;
  cursor: pointer;
`;

// Colors for the workout color selection
const colorOptions = [
  { key: 'red', color: '#D16D6A' },
  { key: 'orange', color: '#ECB576' },
  { key: 'yellow', color: '#F9DA78' },
  { key: 'green', color: '#9DC384' },
  { key: 'blue-ish', color: '#80A4AE' },
  { key: 'blue-ish-ish', color: '#7CA6D7' },
  { key: 'purple', color: '#8B7DBE' },
  { key: 'mauve?', color: '#B87E9E' },
  { key: 'no-color-option', color: 'blank' },
];

const MenuItemHoverable = ({ hoverableItem, onMenuItemClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <MenuItem
      style={{
        paddingTop: 7, paddingBottom: 7, paddingLeft: 16, paddingRight: 16, cursor: 'pointer', background: isHovered ? '#F5F5F5' : 'white',
      }}
      onClick={(e) => onMenuItemClick(hoverableItem.key, e, hoverableItem.closeDropdown)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <IconContainer>
        <div style={{
          display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center',
        }}
        >
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <IcomoonReact
              iconSet={iconSet}
              size={16}
              icon={hoverableItem.icon}
              color={hoverableItem.color || '#9E9E9E'}
              style={hoverableItem.rotation ? { transform: `rotate(${hoverableItem.rotation}deg)` } : {}}
            />
            <Text fontSize='16px'>{hoverableItem.label}</Text>
          </div>
          <IcomoonReact iconSet={iconSet} size={12} icon='right-arrow' color='#9E9E9E' />
        </div>
      </IconContainer>
    </MenuItem>
  );
};

MenuItemHoverable.propTypes = {
  hoverableItem: PropTypes.shape({
    key: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string,
    rotation: PropTypes.number,
    closeDropdown: PropTypes.bool,
  }).isRequired,
  onMenuItemClick: PropTypes.func.isRequired,
};

const MenuItemHoverableAlt = ({ hoverableItem, onMenuItemClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <MenuItem
      key={hoverableItem.key}
      style={{
        paddingTop: 7, paddingBottom: 7, paddingLeft: 14, cursor: 'pointer', background: isHovered ? '#F5F5F5' : 'white',
      }}
      onClick={(e) => onMenuItemClick(hoverableItem.key, e, hoverableItem.closeDropdown)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <IconContainer>
        <IcomoonReact iconSet={iconSet} size={16} icon={hoverableItem.icon} color={hoverableItem.color || '#9E9E9E'} />
        <Text fontSize='16px'>{hoverableItem.label}</Text>
      </IconContainer>
    </MenuItem>
  );
};

MenuItemHoverableAlt.propTypes = {
  hoverableItem: PropTypes.shape({
    key: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string,
    closeDropdown: PropTypes.bool,
  }).isRequired,
  onMenuItemClick: PropTypes.func.isRequired,
};

const SubMenuTitle = ({ item }) => (
  <IconContainer>
    <IcomoonReact
      iconSet={iconSet}
      size={16}
      icon={item.icon}
      color={item.color || '#9E9E9E'}
      style={item.rotation ? { transform: `rotate(${item.rotation}deg)` } : {}}
    />
    <Text fontSize='16px'>{item.label}</Text>
  </IconContainer>
);

SubMenuTitle.propTypes = {
  item: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string,
    rotation: PropTypes.number,
  }).isRequired,
};

const CalendarDropdown = ({ dateOrDay, filteredWeekDates }) => {
  const dropdownRef = useRef(null);
  const colorSubmenuRef = useRef(null);
  const dropdownContainerRef = useRef(null);
  const [maxDropdownHeight, setMaxDropdownHeight] = useState(215);
  const [localDropdownVisible, setLocalDropdownVisible] = useState(false);
  const [selectedColor, setSelectedColor] = useState('');
  const [colorSubmenuShouldDisplayHorizontal, setColorSubmenuShouldDisplayHorizontal] = useState(false);
  const [dropdownHovering, setDropdownHovering] = useState(false);
  const dropdownVisible = useSelector((state) => state.calendar.data.dropdownVisible);
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const expandedItems = useSelector((state) => state.calendar.data.expandedItems);
  const isWeekView = viewType === 'weekView';
  const selectedDate = useSelector((state) => state.calendar.data.selectedDate);
  const effectiveDate = isWeekView || viewType === 'monthView' ? dateOrDay : selectedDate;
  const effectiveDayNumber = isWeekView ? dateOrDay : selectedProgramDayNumber;
  const date = new Date(effectiveDate);
  const endpointFormattedDate = moment(date).format('YYYY-MM-DD');
  const programType = useSelector((state) => state.calendar.data.programType);
  const workoutDetails = programType === 'free-form-program'
    ? useSelector((state) => state.calendar.data.nonDateBasedProgramItems[
      effectiveDayNumber
    ])
    : useSelector((state) => state.calendar.data.calendarItems[endpointFormattedDate]);
  const selectedReduxAthlete = useSelector((state) => state.calendar.data.selectedReduxAthlete);
  const selectedCalendar = useSelector((state) => state.calendar.data.selectedReduxCalendar);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const nonDateBasedProgramItems = useSelector(
    (state) => state.calendar.data.nonDateBasedProgramItems,
  );
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const dispatch = useDispatch();

  useEffect(() => {
    if (workoutDetails?.color) {
      setSelectedColor(workoutDetails.color);
    }
  }, [workoutDetails]);

  const handleMenuItemClick = (key, e, closeDropdown) => {
    if (!closeDropdown) {
      e.domEvent.stopPropagation();
    }
  };

  const handleVisibleChange = (visible) => {
    setLocalDropdownVisible(visible);

    if (visible) {
      const dropdownIcon = dropdownRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const calculatedMaxHeight = windowHeight - dropdownIcon.top - 35;
      const maxHeight = dropdownIcon.top
        - 35 > calculatedMaxHeight ? dropdownIcon.top - 35 : calculatedMaxHeight;
      setMaxDropdownHeight(maxHeight);
    } else {
      lastDayDropdownClosedAt = Date.now();
    }
  };

  const handleUpdateColor = (color) => {
    let submissionObject;
    if (programType === 'free-form-program') {
      submissionObject = {
        color: color === 'blank' ? '' : color,
        title: workoutDetails.title || '',
      };
      const savedWorkoutId = selectedReduxNonDateBasedProgram.id;
      dispatch(updateNonDateBasedWorkoutInfo(
        currentUser.accountCode,
        savedWorkoutId,
        effectiveDayNumber,
        submissionObject,
        'color',
      ));
    } else {
      submissionObject = {
        color: color === 'blank' ? '' : color,
        date: moment(effectiveDate).format('YYYY-MM-DD'),
      };
      if (workoutDetails?.title) {
        submissionObject.title = workoutDetails.title;
      }
      if (Object.keys(selectedReduxAthlete).length) {
        submissionObject.userId = selectedReduxAthlete.id;
      } else {
        submissionObject.calendarId = selectedCalendar.id;
      }

      dispatch(updateWorkoutInfoColor(
        currentUser.accountCode,
        submissionObject,
      ));
    }
  };

  const handleClearWorkoutTitle = () => {
    let submissionObject;
    if (programType === 'free-form-program') {
      submissionObject = {
        color: workoutDetails?.color || '#444444',
        title: '', /** Clear the title by setting it to empty string */
      };
      const savedWorkoutId = selectedReduxNonDateBasedProgram.id;
      dispatch(updateNonDateBasedWorkoutInfo(
        currentUser.accountCode,
        savedWorkoutId,
        effectiveDayNumber,
        submissionObject,
        'name',
      ));
    } else {
      submissionObject = {
        color: workoutDetails?.color || '#444444',
        date: moment(effectiveDate).format('YYYY-MM-DD'),
        title: '', /** Clear the title by setting it to empty string */
      };
      if (Object.keys(selectedReduxAthlete).length) {
        submissionObject.userId = selectedReduxAthlete.id;
      } else {
        submissionObject.calendarId = selectedCalendar.id;
      }
      dispatch(updateWorkoutInfoName(
        currentUser.accountCode,
        submissionObject,
      ));
    }
  };

  useEffect(() => {
    /** close the color sub menu if the parent container closes on resize */
    if (colorSubmenuRef.current && colorSubmenuRef.current.list) {
      colorSubmenuRef.current.list.style.display = localDropdownVisible ? 'flex' : 'none';
    }

    const handleResize = () => {
      if (localDropdownVisible) {
        setLocalDropdownVisible(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [localDropdownVisible, dropdownVisible]);

  useEffect(() => {
    if (!dropdownVisible) {
      setLocalDropdownVisible(false);
    }
  }, [dropdownVisible]);

  /** Close the menu on any scroll while it's open. The trigger is
   * anchored to a small element (a day cell in month view, a header in
   * week view), and an open overlay quickly drifts visually out of place
   * when its container scrolls. Scrolls inside the menu's own list
   * (the maxDropdownHeight overflowY area) are excluded so the user
   * can scroll long menus normally. Capture phase + passive so we hear
   * scrolls on every nested container without affecting performance. */
  useEffect(() => {
    if (!localDropdownVisible) return undefined;
    const handleScroll = (e) => {
      if (dropdownContainerRef.current && dropdownContainerRef.current.contains(e.target)) {
        return;
      }
      setLocalDropdownVisible(false);
    };
    document.addEventListener('scroll', handleScroll, { capture: true, passive: true });
    return () => document.removeEventListener('scroll', handleScroll, { capture: true });
  }, [localDropdownVisible]);

  useEffect(() => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      const distanceFromBottom = window.innerHeight - rect.bottom;

      if (distanceFromBottom <= 500) {
        setColorSubmenuShouldDisplayHorizontal(true);
      } else {
        setColorSubmenuShouldDisplayHorizontal(false);
      }
    }
  }, [dropdownRef.current, dropdownVisible, localDropdownVisible]);

  const handleColorSelect = (e, color) => {
    e.domEvent.stopPropagation();
    setSelectedColor(color);
    handleUpdateColor(color);
  };

  const handleExpandExercises = () => {
    const newExpandedItems = { ...expandedItems };
    if (workoutDetails?.exercises?.length) {
      workoutDetails.exercises.forEach((exercise) => {
        if (exercise?.type === 'session_break' && exercise.assignedId) {
          newExpandedItems[exercise.assignedId] = true;
        }
      });
    }
    dispatch(setExpandedItems(newExpandedItems));
  };

  const handleCollapseExercises = () => {
    const newExpandedItems = { ...expandedItems };
    if (workoutDetails?.exercises?.length) {
      workoutDetails.exercises.forEach((exercise) => {
        if (exercise?.type === 'session_break' && exercise.assignedId) {
          newExpandedItems[exercise.assignedId] = false;
        }
      });
    }
    dispatch(setExpandedItems(newExpandedItems));
  };

  const hasSessionBreaks = !!workoutDetails?.exercises?.some(
    (exercise) => exercise?.type === 'session_break' && exercise.assignedId,
  );

  const colorSubmenu = (
    <Menu
      ref={colorSubmenuRef}
      className='color-submenu'
      style={{
        padding: 8,
        top: colorSubmenuShouldDisplayHorizontal ? 0 : 118,
        left: colorSubmenuShouldDisplayHorizontal ? -270 : -47,
        backgroundColor: '#FFFFFF',
        display: 'flex',
        flexDirection: colorSubmenuShouldDisplayHorizontal ? 'row' : 'column',
        border: 'none',
        borderRadius: 4,
        width: 'fit-content',
        gap: 4,
        marginTop: 0,
        marginBottom: 0,
        alignItems: 'center',
      }}
    >
      {colorOptions.map((color) => {
        if (color.color !== 'blank') {
          return (
            <MenuItem
              key={color.key}
              onClick={(e) => handleColorSelect(e, color.color)}
            >
              <ColorIconContainer>
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 4,
                    backgroundColor: color.color,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    cursor: 'pointer',
                  }}
                >
                  {color.color === selectedColor ? (
                    <IcomoonReact
                      iconSet={iconSet}
                      size={12}
                      icon='checkmark'
                      color='white'
                    />
                  ) : null}
                </div>
              </ColorIconContainer>
            </MenuItem>
          );
        }
        return (
          <MenuItem key={color.key} onClick={(e) => handleColorSelect(e, color.color)}>
            <NoColorIconContainer>
              <IcomoonReact
                iconSet={iconSet}
                size={12}
                icon='opt-out'
                color='#9E9E9E'
              />
            </NoColorIconContainer>
          </MenuItem>
        );
      })}
    </Menu>
  );

  const shouldShowLegacy = workoutDetails?.itemsOnDateUseLegacyOrder;

  const menuItems = [
    {
      key: '1', icon: 'expand2', label: 'Expand Session Breaks', shortcut: '⌘+E', closeDropdown: true, disabled: viewType === 'monthView', isStatic: !hasSessionBreaks,
    },
    {
      key: '1a', icon: 'collapse', label: 'Collapse Session Breaks', closeDropdown: true, disabled: viewType === 'monthView', isStatic: !hasSessionBreaks,
    },
    { divider: true },
    {
      key: '2', icon: 'duplicate', label: 'Copy', shortcut: '⌘+C', closeDropdown: false, disabled: programType === 'free-form-program',
    },
    {
      key: '5',
      icon: 'save2',
      label: 'Save',
      shortcut: '⌘+S',
      closeDropdown: false,
      disabled: programType === 'free-form-program',
    },
    {
      key: '6',
      icon: 'send-alt',
      label: 'Share Workout',
      rotation: -45,
      closeDropdown: false,
      disabled: programType === 'free-form-program',
    },
    {
      key: '7',
      icon: 'pencil',
      label: 'Change Workout Color',
      rotation: 90,
      submenu: colorSubmenu,
      closeDropdown: false,
    },
    {
      key: '7a', icon: 'remove', label: 'Clear Workout Title', closeDropdown: true,
    },
    ...(workoutDetails?.exercises?.length && programType !== 'free-form-program'
      ? [
        { divider: true },
      ]
      : []),
    ...((shouldShowLegacy && programType !== 'free-form-program' && workoutDetails?.exercises?.length)
      ? [
        {
          key: '7b',
          icon: 'info2',
          label: 'Items Use Legacy Order',
          closeDropdown: false,
          isStatic: false,
        },
      ]
      : []),
    ...((!shouldShowLegacy && workoutDetails?.exercises?.length && programType !== 'free-form-program')
      ? [
        {
          key: '7c',
          icon: 'restart',
          label: 'Reset Order',
          closeDropdown: false,
          isStatic: false,
        },
      ]
      : []),
    { divider: true },
    {
      key: '8', icon: 'delete', label: 'Clear Workout Items', color: 'red', closeDropdown: true,
    },
  ];

  const renderMenuItems = () => menuItems.map((item, index) => {
    if (item.disabled) return null;
    if (item.divider) return <CustomDivider key={`divider-${index}`} />;
    if (item.submenu) {
      return (
        <Dropdown
          key={item.key}
          trigger={['click']}
          overlay={item.submenu}
          placement='rightTop'
        >
          <MenuItem
            style={{
              paddingTop: 7, paddingBottom: 7, paddingLeft: 14, cursor: 'pointer', background: 'white',
            }}
            onClick={(e) => e.domEvent.stopPropagation()}
          >
            <IconContainer>
              <div style={{
                display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center',
              }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <IcomoonReact
                    iconSet={iconSet}
                    size={16}
                    icon={item.icon}
                    color={item.color || '#9E9E9E'}
                    style={item.rotation ? { transform: `rotate(${item.rotation}deg)` } : {}}
                  />
                  <Text fontSize='16px'>{item.label}</Text>
                </div>
                <IcomoonReact iconSet={iconSet} size={12} icon='right-arrow' color='#9E9E9E' />
              </div>
            </IconContainer>
          </MenuItem>
        </Dropdown>
      );
    }

    if (item.isStatic) {
      return (
        <div
          key={item.key}
          style={{
            paddingTop: 7,
            paddingBottom: 7,
            paddingLeft: 14,
            paddingRight: 14,
            display: 'flex',
            alignItems: 'center',
            background: 'white',
            color: '#9E9E9E',
            cursor: 'not-allowed',
          }}
        >
          <IconContainer>
            <IcomoonReact
              iconSet={iconSet}
              size={16}
              icon={item.icon}
              color='#9E9E9E'
            />
            <Text fontSize='16px' color='#9E9E9E'>{item.label}</Text>
          </IconContainer>
        </div>
      );
    }

    // Handle the "Lecay Item" info modal
    if (item.key === '7b') {
      return (
        <MenuItemHoverableAlt
          key={item.key}
          hoverableItem={item}
          onMenuItemClick={() => {
            dispatch(setActiveModal('itemsUseLegacyOrderInfoModal', {}));
          }}
        />
      );
    }

    // Handle the "Reset Order" menu item
    if (item.key === '7c') {
      return (
        <MenuItemHoverableAlt
          key={item.key}
          hoverableItem={item}
          onMenuItemClick={() => {
            const modalData = {
              endpointFormattedDate,
            };
            dispatch(setActiveModal('confirmResetOrderModal', modalData));
            setLocalDropdownVisible(false);
          }}
        />
      );
    }

    if (item.key === '1') {
      return (
        <MenuItemHoverableAlt
          key={item.key}
          hoverableItem={item}
          onMenuItemClick={() => {
            handleExpandExercises();
            setLocalDropdownVisible(false);
          }}
        />
      );
    }

    if (item.key === '1a') {
      return (
        <MenuItemHoverableAlt
          key={item.key}
          hoverableItem={item}
          onMenuItemClick={() => {
            handleCollapseExercises();
            setLocalDropdownVisible(false);
          }}
        />
      );
    }

    // Handle the "Share Workout" menu item specifically
    if (item.key === '6') {
      return (
        <MenuItemHoverableAlt
          key={item.key}
          hoverableItem={item}
          onMenuItemClick={() => {
            // Make the API request to get share link
            let shareData;
            if (programType === 'free-form-program') {
              const modalData = {
                weekViewDayNumber: effectiveDayNumber,
                workoutDetails,
                isFromWeekView: isWeekView,
              };
              dispatch(setActiveModal('shareWorkoutModal', modalData));
              shareData = {
                dayNumber: effectiveDayNumber,
                programId: selectedReduxNonDateBasedProgram?.id,
              };
            } else {
              // Open the modal with modalData for week view context
              const modalData = isWeekView || viewType === 'monthView' ? {
                weekViewDate: effectiveDate,
              } : null;
              if (isWeekView) {
                modalData.isFromWeekView = true;
              }
              dispatch(setActiveModal('shareWorkoutModal', modalData));
              shareData = {
                date: moment(effectiveDate).format('YYYY-MM-DD'),
              };
              // Add calendarId or userId if available
              if (Object.keys(selectedReduxAthlete).length) {
                shareData.userId = selectedReduxAthlete.id;
              } else {
                shareData.calendarId = selectedCalendar.id;
              }
              dispatch(shareWorkoutInfo(currentUser.accountCode, shareData));
            }
            /** Close dropdown after opening modal */
            setLocalDropdownVisible(false);
          }}
        />
      );
    }

    // Handle the "Save" menu item specifically
    if (item.key === '5') {
      return (
        <MenuItemHoverableAlt
          key={item.key}
          hoverableItem={item}
          onMenuItemClick={() => {
            // Pass the effective date as modalData when in week view or month view
            const modalData = isWeekView || viewType === 'monthView' ? {
              date: effectiveDate,
            } : null;
            if (isWeekView) {
              modalData.isFromWeekView = true;
            }
            dispatch(setActiveModal('saveWorkoutModal', modalData));
            // Close dropdown after opening modal
            setLocalDropdownVisible(false);
          }}
        />
      );
    }

    // Handle the "Clear Workout Items" menu item
    if (item.key === '8') {
      return (
        <MenuItemHoverableAlt
          key={item.key}
          hoverableItem={item}
          onMenuItemClick={() => {
            if (programType === 'free-form-program') {
              const exercises = workoutDetails?.exercises;
              // Items where assignedId is a string (pre-saved)
              const preSaveItems = exercises.filter(
                (exerciseItem) => typeof exerciseItem?.assignedId === 'string',
              );

              // Items where assignedId is NOT a string
              const savedItems = exercises.filter(
                (exerciseItem) => typeof exerciseItem?.assignedId !== 'string',
              );
              if (exercises?.length) {
                dispatch(setActiveModal('confirmDeleteModal', {
                  programName: selectedReduxNonDateBasedProgram?.programName,
                  weekNumber: workoutDetails.weekNumber,
                  dayInWeek: workoutDetails.dayInWeek,
                  isBulkDelete: true,
                  bulkDeleteAssignedIds: savedItems?.map((exercise) => exercise?.assignedId),
                  ...(programType === 'free-form-program' && { dayNumber: effectiveDayNumber }),
                  preSaveItems,
                }));
              }
            } else {
            // Pass the effective date as modalData when in week view
              const modalData = isWeekView || viewType === 'monthView' ? {
                date: effectiveDate,
              } : null;
              if (isWeekView) {
                modalData.isFromWeekView = true;
              }
              dispatch(setActiveModal('clearWorkoutItemsModal', modalData));
              // Close dropdown after opening modal
              setLocalDropdownVisible(false);
            }
          }}
        />
      );
    }

    // Handle the "Clear Workout Title" menu item
    if (item.key === '7a') {
      return (
        <MenuItemHoverableAlt
          key={item.key}
          hoverableItem={item}
          onMenuItemClick={() => {
            handleClearWorkoutTitle();
            // Close dropdown after clearing title
            setLocalDropdownVisible(false);
          }}
        />
      );
    }

    // Handle the "Copy" menu item specifically
    if (item.key === '2') {
      return (
        <MenuItemHoverableAlt
          key={item.key}
          hoverableItem={item}
          onMenuItemClick={() => {
            // Calculate week range when in week view
            let modalData = null;
            if (isWeekView) {
              // Find the clicked date in filteredWeekDates by comparing dates
              const clickedDate = moment(date);
              const weekStartDate = filteredWeekDates.find((momentDate) => momentDate.isSame(clickedDate, 'day')) || filteredWeekDates[0];
              const weekEndDate = filteredWeekDates[filteredWeekDates.length - 1];
              modalData = {
                weekStartDate: weekStartDate.toDate(),
                weekEndDate: weekEndDate.toDate(),
                isFromWeekView: true,
                currentViewedDate: effectiveDate,
              };
            }

            // Auto-preselect all items for Program Library mode
            if (programType === 'free-form-program' && nonDateBasedProgramItems) {
              const dayData = nonDateBasedProgramItems[effectiveDayNumber];
              if (dayData && dayData.exercises && dayData.exercises.length > 0) {
                const preSelectedItems = {};
                dayData.exercises.forEach((exercise) => {
                  // Only include items with numeric assignedId (filter out strings)
                  if (exercise.assignedId && typeof exercise.assignedId === 'number') {
                    preSelectedItems[exercise.assignedId] = exercise;
                  }
                });

                modalData = {
                  ...modalData,
                  preSelectedItems,
                  skipFirstSlide: true,
                  sourceDayNumber: effectiveDayNumber,
                  sourceWeekNumber: dayData.weekNumber,
                  sourceDayInWeek: dayData.dayInWeek,
                  currentViewedDate: effectiveDate,
                };
              }
            }

            // Ensure modalData includes currentViewedDate for day view
            if (!modalData) {
              modalData = {
                currentViewedDate: effectiveDate,
              };
            }

            dispatch(setActiveModal('copyWorkoutModal', modalData));
            // Close dropdown after opening modal
            setLocalDropdownVisible(false);
          }}
        />
      );
    }

    // Handle the "duplicate" menu item
    if (item.key === '3') {
      return (
        <MenuItemHoverableAlt
          key={item.key}
          hoverableItem={item}
          onMenuItemClick={() => {
            // add day the same what we do for the side bar, and then add an item to the day
            // if have presave items on page do we disable the duplicate or just not carry those over
            setLocalDropdownVisible(false);
          }}
        />
      );
    }

    return (
      <MenuItemHoverableAlt key={item.key} hoverableItem={item} onMenuItemClick={handleMenuItemClick} />
    );
  });

  return (
    <Dropdown
      trigger={['click']}
      overlay={(
        <MenuWrapper ref={dropdownContainerRef}>
          <Menu
            mode='vertical'
            style={{
              width: 280,
              minWidth: 0,
              maxHeight: maxDropdownHeight,
              overflowY: 'auto',
              marginBottom: 0,
            }}
          >
            {renderMenuItems()}
          </Menu>
        </MenuWrapper>
      )}
      visible={localDropdownVisible}
      onVisibleChange={handleVisibleChange}
    >
      <DropdownContainer
        ref={dropdownRef}
        onMouseEnter={() => setDropdownHovering(true)}
        onMouseLeave={() => setDropdownHovering(false)}
      >
        <DropdownIcon>
          <IcomoonReact
            iconSet={iconSet}
            size={18}
            icon='dots'
            color={dropdownHovering ? '#77A7FF' : '#9E9E9E'}
            style={{ transform: 'rotate(90deg)' }}
          />
        </DropdownIcon>
      </DropdownContainer>
    </Dropdown>
  );
};

CalendarDropdown.propTypes = {
  dateOrDay: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  filteredWeekDates: PropTypes.instanceOf(Array),
};

CalendarDropdown.defaultProps = {
  dateOrDay: null,
  filteredWeekDates: [],
};

export default CalendarDropdown;
